import React from 'react'
import { FormikActions } from 'formik'
import { Auth } from 'aws-amplify'
import 'react-bootstrap-typeahead/css/Typeahead.css'
import { CognitoUser } from 'amazon-cognito-identity-js'
import { NotificationType } from '../../components/Notifications'
import { withNotificationContext } from '../../contexts/withNotificationContext'
import { SetNotification } from '../../contexts/NotificationProvider'
import { HasGroup, UserGroup, withArticlesContext } from '../../contexts'
import './AuthenticCheckPage.scss'
import { AuthenticCheckResults } from './AuthenticCheckResults'
import { AuthenticCheckModal } from './AuthenticCheckModal'
import { AuthenticCheckForm, AuthenticCheckFormValues } from './AuthenticCheckForm'
import loadingBox from '../../assets/images/loading_box.gif'
import { Article, ArticlesData, Trace, TracesData } from '../../types/DataTypes'
import isObjectEmpty from '../../utils/isObjectEmpty'
import { API } from '../../utils/api'

enum AuthenticWatchStatus {
  NotChecked,
  Authentic,
  NotAuthentic,
}

interface AuthenticDataResponse {
  StatusCode: number
  Payload: string
  ExecutedVersion: string
  FunctionError?: string
}

interface AuthenticStatusErrorResponse {
  errorMessage: string
}

interface AuthenticStatuses {
  noOfSearches: boolean
  logicCheck: boolean
  traceTypeNotStolenOrDiscarded: boolean
  serialExistsinBatchTableAndMatching: boolean
}

type AuthenticStatusResponse = AuthenticStatusErrorResponse | AuthenticStatuses

function isErrorResponse(
  authStatusResponse: AuthenticStatusResponse,
): authStatusResponse is AuthenticStatusErrorResponse {
  return (authStatusResponse as AuthenticStatusErrorResponse).errorMessage !== undefined
}

interface AuthenticCheckData {
  EAN: string
  batchNo: string
  serialNo: string
  userId: string
  articleNo: string
}

interface Props {
  setNotification: SetNotification
  articles: any[]
  setArticles: any
  hasGroup: HasGroup
  userData: CognitoUser
}

interface State {
  articleNoSource: Article[]
  isConfirmationModalOpened: boolean
  traces: Trace[]
  isAuthentic: AuthenticWatchStatus
  falseDescription: string
}

const AUTHENTIC_SEARCH_EXTENDED_ROLES = [UserGroup.SuperAdmin, UserGroup.Admin, UserGroup.BrandProtectionExtended]

const ERROR_STATUSES: { [key: string]: string } = {
  logicCheck: 'Logic check did not pass',
  traceTypeNotStolenOrDiscarded: 'Type check did not pass, watch is stolen or discarded',
  noOfSearches: 'Too many searches made on this serial',
  serialExistsinBatchTableAndMatching: 'Serial does not exist in batch table or not matching with batch',
}

interface StatusResponseOutput {
  errorMessages: string[]
  isAuthentic: AuthenticWatchStatus
}

class AuthenticCheckComponent extends React.Component<Props, State> {
  formSubmitCallback: any

  state: State = {
    articleNoSource: [],
    isConfirmationModalOpened: false,
    traces: [],
    isAuthentic: AuthenticWatchStatus.NotChecked,
    falseDescription: '',
  }

  componentDidMount(): void {
    if (this.props.articles && !isObjectEmpty(this.props.articles)) {
      this.setState({
        articleNoSource: Object.values(this.props.articles),
      })
      return
    }
    this.getArticleNoAutoComplete().then((data: ArticlesData) => {
      if (!data || !data.items) {
        this.props.setNotification({
          text: 'Failed to get article number list. Please try again later.',
          type: NotificationType.ERROR,
        })
        return
      }

      this.props.setArticles(data.items)
      this.setState({
        articleNoSource: data.items,
      })
    })
  }

  componentWillUnmount(): void {
    this.props.setNotification(null)
  }

  render() {
    return (
      <div data-component="authenticpage-component">
        <div className="row">
          <div className="col-md-offset-3 col-md-6">
            <div className="panel reference-box">
              <div className="panel-heading">Authentic Check</div>
              <div className="panel-body">
                {!!this.state.articleNoSource.length ? (
                  <AuthenticCheckForm handleSubmit={this.handleSubmit} articleNoSource={this.state.articleNoSource} />
                ) : (
                  <img src={loadingBox} className="loading" alt="loading" />
                )}
              </div>
            </div>
          </div>
        </div>

        {this.state.isAuthentic !== AuthenticWatchStatus.NotChecked &&
          (this.state.isAuthentic === AuthenticWatchStatus.Authentic ? (
            <div className="message valid animated fadeIn">
              <span className="glyphicon glyphicon-check" /> The information is consistent with a Daniel Wellington
              product but this doesn't guarantee that it is a genuine product
            </div>
          ) : (
            <div className="message invalid animated fadeIn">
              <span className="glyphicon glyphicon-ban-circle" />
              <span>{this.state.falseDescription}</span>
            </div>
          ))}

        {this.props.hasGroup(AUTHENTIC_SEARCH_EXTENDED_ROLES) && this.state.traces.length > 0 && (
          <AuthenticCheckResults traces={this.state.traces} />
        )}

        <AuthenticCheckModal
          close={() => this.setState({ isConfirmationModalOpened: false })}
          formSubmitCallback={this.formSubmitCallback}
          isConfirmationModalOpened={this.state.isConfirmationModalOpened}
          title={
            <>
              <span className="glyphicon glyphicon-exclamation-sign" /> Warning
            </>
          }
        >
          The search you make will be logged and reviewed.
        </AuthenticCheckModal>
      </div>
    )
  }

  private getArticleNoAutoComplete = (): Promise<ArticlesData> => {
    return API.get('its-items', '/', {}).catch(() => {
      this.props.setNotification({
        text: 'Failed to get article number list. Please try again later.',
        type: NotificationType.ERROR,
      })
    })
  }

  private handleSubmit = (
    formValues: AuthenticCheckFormValues,
    actions: FormikActions<AuthenticCheckFormValues>,
  ): void => {
    actions.setSubmitting(false)

    this.formSubmitCallback = () => {
      actions.setSubmitting(true)
      this.search(formValues, actions)
    }

    this.setState({ isConfirmationModalOpened: true })
  }

  private search = (formValues: AuthenticCheckFormValues, actions: FormikActions<AuthenticCheckFormValues>) => {
    this.reset()
    const authenticCheckData: AuthenticCheckData = this.buildAuthenticCheckData(formValues)

    this.authenticSearch(authenticCheckData)
      .then((authenticData: AuthenticDataResponse) => {
        const authenticStatus: AuthenticStatusResponse = JSON.parse(authenticData.Payload)

        if (!isErrorResponse(authenticStatus)) {
          const { errorMessages, isAuthentic } = this.handleResponse(authenticStatus)
          this.setState({ falseDescription: errorMessages.join(' - '), isAuthentic })
          if (!this.props.hasGroup(AUTHENTIC_SEARCH_EXTENDED_ROLES)) return
          this.getSerialInfo(formValues.serial).then((data: TracesData) => {
            if (data && data.Items) {
              this.setState({ traces: data.Items })
            }
          })
          return
        }

        if (
          (isErrorResponse(authenticStatus) && authenticStatus.errorMessage === 'Not Found') ||
          authenticData.StatusCode === 404
        ) {
          const { errorMessages, isAuthentic } = this.handleResponse(authenticStatus, authenticData.StatusCode)
          this.setState({ falseDescription: errorMessages[0], isAuthentic })
          return
        }

        this.setState({
          isAuthentic: AuthenticWatchStatus.NotAuthentic,
          falseDescription: 'Error in check - please try again or contact admins',
        })
      })
      .finally(() => actions.setSubmitting(false))
  }

  private getSerialInfo = (serialNo: string): Promise<TracesData> => {
    return API.get('its', '/tracing/serial', { queryStringParameters: { searchParam: serialNo } })
  }

  private buildAuthenticCheckData = (formValues: AuthenticCheckFormValues): AuthenticCheckData => {
    const article: Article | undefined = this.state.articleNoSource.find(
      (item: Article) => item.SKU === formValues.article,
    )

    return {
      EAN: article ? article.GTIN : '',
      batchNo: formValues.batch.toUpperCase(),
      serialNo: formValues.serial,
      userId: this.props.userData.getUsername(),
      articleNo: formValues.article,
    }
  }

  private authenticSearch = (authenticCheckData: AuthenticCheckData): Promise<AuthenticDataResponse> => {
    return Auth.currentSession()
      .then(session => Promise.all([API.endpoint('its-authcheck'), session.getIdToken().getJwtToken()]))
      .then(([apiEndpoint, jwtToken]) =>
        fetch(apiEndpoint, {
          mode: 'cors',
          headers: {
            Authorization: jwtToken,
            'Content-Type': 'application/json',
          },
          method: 'POST',
          body: JSON.stringify(authenticCheckData),
        }),
      )
      .then(res => {
        // Transform response to look like what I think is the default Amplify-responses
        return res.text().then(body => {
          const authenticDataResponse: AuthenticDataResponse = {
            StatusCode: res.status,
            Payload: body,
            ExecutedVersion: '???',
          }
          if (!res.ok) {
            authenticDataResponse.Payload = JSON.stringify({ errorMessage: JSON.parse(body).message })
          }
          return authenticDataResponse
        })
      })
  }

  private handleResponse = (
    authenticStatusResponse: AuthenticStatusResponse,
    statusCode?: number,
  ): StatusResponseOutput => {
    if (
      (isErrorResponse(authenticStatusResponse) && authenticStatusResponse.errorMessage === 'Not Found') ||
      (statusCode && statusCode === 404)
    ) {
      return { errorMessages: ['The product is a counterfeit'], isAuthentic: AuthenticWatchStatus.NotAuthentic }
    }

    return Object.entries(authenticStatusResponse).reduce(
      (acc: StatusResponseOutput, [status, statusValue]) => {
        if (statusValue) return acc

        acc.isAuthentic = AuthenticWatchStatus.NotAuthentic

        if (!acc.errorMessages.includes('The product is a counterfeit')) {
          acc.errorMessages.push('The product is a counterfeit')
        }

        if (!this.props.hasGroup(AUTHENTIC_SEARCH_EXTENDED_ROLES)) return acc
        if (!ERROR_STATUSES[status]) return acc

        acc.errorMessages.push(ERROR_STATUSES[status])
        return acc
      },
      { errorMessages: [], isAuthentic: AuthenticWatchStatus.Authentic },
    )
  }

  private reset = () => {
    this.setState({ traces: [], isAuthentic: AuthenticWatchStatus.NotChecked })
  }
}

const pageWithNotificationContext = withNotificationContext(AuthenticCheckComponent)

export const AuthenticCheckPage = withArticlesContext(pageWithNotificationContext)
